import Icon from '@/components/ui/AppIcon';

interface LoyaltyReward {
  id: string;
  title: string;
  description: string;
  points: number;
  icon: string;
  status: 'available' | 'redeemed' | 'locked';
}

interface LoyaltyRewardsSectionProps {
  totalPoints: number;
  rewards: LoyaltyReward[];
}

const LoyaltyRewardsSection = ({ totalPoints, rewards }: LoyaltyRewardsSectionProps) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'available':
        return 'bg-success/10 text-success border-success';
      case 'redeemed':
        return 'bg-muted text-muted-foreground border-border';
      case 'locked':
        return 'bg-warning/10 text-warning border-warning';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6 shadow-elevation-sm">
      <div className="flex items-center gap-3 mb-6">
        <div className="flex items-center justify-center w-10 h-10 bg-accent/10 rounded-lg">
          <Icon name="GiftIcon" size={24} className="text-accent" />
        </div>
        <div>
          <h2 className="font-heading font-semibold text-lg text-foreground">Loyalty & Rewards</h2>
          <p className="text-sm text-muted-foreground">Earn points with every purchase</p>
        </div>
      </div>

      <div className="p-6 rounded-lg bg-gradient-to-br from-primary to-accent text-primary-foreground mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-sm opacity-90 mb-1">Available Points</p>
            <p className="font-data font-bold text-4xl">{totalPoints.toLocaleString('en-IN')}</p>
          </div>
          <Icon name="SparklesIcon" size={48} className="opacity-80" />
        </div>
        <div className="flex items-center gap-2 text-sm opacity-90">
          <Icon name="InformationCircleIcon" size={16} />
          <span>Earn 1 point for every ₹10 spent</span>
        </div>
      </div>

      <div className="space-y-3">
        {rewards.map((reward) => (
          <div
            key={reward.id}
            className={`p-4 rounded-lg border transition-smooth ${getStatusColor(reward.status)}`}
          >
            <div className="flex items-start gap-4">
              <div className="flex items-center justify-center w-12 h-12 bg-card rounded-lg">
                <Icon name={reward.icon as any} size={24} className="text-primary" />
              </div>
              <div className="flex-1">
                <div className="flex items-start justify-between gap-4 mb-2">
                  <div>
                    <h3 className="font-medium text-sm text-foreground mb-1">{reward.title}</h3>
                    <p className="text-xs text-muted-foreground">{reward.description}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-data font-semibold text-base text-primary">{reward.points}</p>
                    <p className="text-xs text-muted-foreground">points</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {reward.status === 'available' && (
                    <button className="px-4 py-1.5 bg-primary text-primary-foreground rounded-md font-medium text-xs transition-smooth hover:shadow-elevation-sm">
                      Redeem Now
                    </button>
                  )}
                  {reward.status === 'redeemed' && (
                    <span className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Icon name="CheckCircleIcon" size={14} />
                      Redeemed
                    </span>
                  )}
                  {reward.status === 'locked' && (
                    <span className="flex items-center gap-1 text-xs text-warning">
                      <Icon name="LockClosedIcon" size={14} />
                      Need {reward.points - totalPoints} more points
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default LoyaltyRewardsSection;